// クラシック版のパーツの一覧（段階 C の2 の 3。2026-10-25 に chibi.js の setPartsList・selPartsStyle・getSelLayerId・getPartsLayerList から描き直した。見た目・動きは前と同じ）。
// カテゴリを選ぶと setCategory が呼ぶ。svg#chaSvgParts の中に、ボタンの枠 rect.chibiPartsBtnR#pbr<番号>（選んでいるものに class selected）、
// パーツごとの g.g_root（パーツの絵の層・押す所 rect.chibiPartsBtn#pb<番号>・番号の文字）、選んでいる印 image#selcheck を置く（どれも前と同じ並び・属性）。
// PC のスクロールバー（mCustomScrollbar。作るのは起動の init）・スマホの横のスクロールは fx の窓口から
import type { Category } from '../types.js';
import { chibi, common } from './engine.js';
import { addClass, appendSvg, bindData, onClick, removeClass, svgEl } from './dom.js';
import * as fx from './fx.js';
import type { Selection } from './selection.js';

/** パーツ1つ（番号と、id の頭が x・a・y の層）。Pos は一覧の中のボタンの位置（setPartsList が入れる） */
export interface ListPart {
  id: number;
  x?: Element;
  a?: Element;
  y?: Element;
  Pos?: { X: number; Y: number; W: number; H: number };
}
interface Scale {
  x(v: number): number;
  y(v: number): number;
}
interface PartsEngine {
  isMobile: boolean;
  DIV_F_PARTS: string;
  DIV_F_PARTS_SC: string;
  DIV_F_CONTENTS: string;
  SVG_PARTS: string;
  ATTR_G_ROOT: string;
  ATTR_SELECTED: string;
  ATTR_SEL_CHECK: string;
  ATTR_SAVEKEY: string;
  FILE_CHECK: string;
  PARTS_CHARA: string;
  PARTS_BG: string;
  PARTS_TEXT: string;
  PARTS_MASK: string;
  PARTS_MASKED: string[];
  SIZE_PARTS_ICON: number;
  SIZE_CHARAT_IMAGE: number;
  COLOR_PID_NAME: string;
  COLOR_PID_STED: string;
  COLOR_PID_NORM: string;
  STROKE_WIDTH: unknown;
  MODE_PATTERN: unknown;
  scParts: Scale;
  svgParts: Selection | null;
  svgMake: Selection;
  svgBg: Selection;
  selLayer: Selection;
  selLayer2: Selection | null;
  selLayer3: Selection | null;
  selCategory: Category;
  btnEditLR: { RESET(v: boolean): void };
  btnSubMove: { RESET(v: boolean): void };
  btnSubReverse: { RESET(v: boolean): void };
  btnSubThick: { EXEC(layer: string): void };
  btnMenuName: { SETMODE(id: number): void };
  btnEditParts: { ON_PARTS(): void };
  config<T>(key: string, def: T): T;
  coreOps(): { part(cat: Category, id: number, anim: boolean): void };
  getPartsLayerList(aCname: string, aParts: { SVG: string }, anId?: unknown): ListPart[];
  getSelLayerId(): number;
  selPartsStyle(anId: unknown, aX?: number, aY?: number, aW?: number, aH?: number): void;
  setPartsColorPattern(svg: Selection, cat: Category, ptn: string, prev: string, mode: string, col: string): void;
  applyMask(svg: Selection, layerKey: string): void;
  getChara(id: unknown): { id: unknown; name?: string };
  loadChara(key: string, b: boolean): boolean;
  load(id: unknown): void;
}
const E = (): PartsEngine => chibi() as unknown as PartsEngine;
type Common = {
  nowChangeParts: boolean;
  getAllGfromSVG(svgText: string, id?: string): ArrayLike<Element>;
  getPartsNoFromG(id: string): number;
  getXYfromIndex(i: number, row: number, w: number, h: number, wmax: number, horizon: boolean): { X: number; Y: number; W?: number; H?: number };
  changeParts(svg: Selection, layerKey: string, layer: string[], effectNo: number, callback: () => void, corePart: () => void): void;
  openDialog(mode: string, mes: string[], ok: () => void): void;
};
const C = (): Common => common() as unknown as Common;

/** 文字と数を前と同じく == で比べる（属性の値は文字、パーツ番号は数） */
const looseEq = (a: unknown, b: unknown): boolean => a == b;

/** パーツの層の頭文字（aTgLayer[anIndex]。空ならどれも x）の層があるか・その層（charat.common の checkLayer・getLayer と同じ） */
function layerOf(d: ListPart, aTgLayer: string[], anIndex: number): Element | undefined {
  if (aTgLayer.length == 0) return d.x;
  const t = aTgLayer[anIndex];
  return t == 'x' ? d.x : t == 'y' ? d.y : t == 'a' ? d.a : undefined;
}

/**
 * パーツの一覧を作り直す。aCname: カテゴリ名、aReverse: 1 なら絵を左右反転、aScale: 絵の切り出し [横の寄せ, 縦の寄せ, 拡大]、aParts: カテゴリのパーツの SVG、
 * aW・aH: ボタン 1 つ分の大きさ、aRow: 段の数、aLayerKey・aLayer: 差しかえるレイヤー、aTgLayer: レイヤーごとの層の頭文字、aCategory: カテゴリ
 */
export function setPartsList(
  aCname: string,
  aReverse: number,
  aScale: number[],
  aParts: { SVG: string },
  aW: number,
  aH: number,
  aRow: number,
  aLayerKey: string,
  aLayer: string[],
  aTgLayer: string[],
  aCategory: Category,
): void {
  const e = E(), sbar = e.DIV_F_PARTS_SC;
  // スクロールバー調整
  if (!e.isMobile) {
    fx.scrollbarDisable(sbar, true);
    fx.setCssIn(sbar, '#mCSB_2_container', 'width', 0);
  }
  // スマホは作り直す間パーツの一覧を隠し、最後に ON_PARTS で出す（パーツの一覧をパーツのボタンで出し入れする第1世代の front 型＝PARTS_LIST_BY_BUTTON は隠さない）
  if (e.isMobile && !e.config('PARTS_LIST_BY_BUTTON', false)) fx.setCss(sbar, 'display', 'none');
  // パーツの領域を作り直す
  const old = e.svgParts != null ? e.svgParts.node() : null;
  if (old && old.parentNode) old.parentNode.removeChild(old);
  const list = e.getPartsLayerList(aCname, aParts);
  const pWidthMax = Math.ceil(list.length / aRow);
  const svg = appendSvg(e.DIV_F_PARTS, e.SVG_PARTS, pWidthMax * e.scParts.x(e.SIZE_PARTS_ICON), aRow * e.scParts.y(e.SIZE_PARTS_ICON));
  e.svgParts = fx.d3Select(svg);
  // ボタンの内側の余白（元ソース・storm は /18、共通版は /36 で余白が半分）
  const padDiv = e.config('PARTS_BTN_PAD', 36);
  const pad = { X: aW / padDiv, Y: aH / padDiv };
  const rsize = aW / 5;
  // ボタンの背景（角丸の枠。位置 d.Pos をここで決めて、あとの処理でも使う）
  list.forEach((d, i) => {
    const p = C().getXYfromIndex(i, aRow, aW, aH, aRow, false);
    p.X += pad.X;
    p.Y += pad.Y;
    p.W = aW - pad.X * 2;
    p.H = aH - pad.Y * 2;
    d.Pos = p as ListPart['Pos'];
    if (!svg) return;
    const r = svgEl('rect', { class: 'chibiPartsBtnR', id: 'pbr' + Number(d.id), x: p.X, y: p.Y, width: p.W, height: p.H, rx: rsize, ry: rsize, stroke: 'lightgray' });
    bindData(r, d);
    svg.appendChild(r);
  });
  // パーツの絵の置き方（表裏共通）。500 の絵をボタンの大きさに縮め、aScale で拡大・寄せる
  const ptransform = (d: ListPart): string => {
    const pos = d.Pos!;
    const xmgn = (pos.W * aScale[2] - pos.W) * aScale[0];
    const ymgn = (pos.H * aScale[2] - pos.H) * aScale[1];
    const sx = (pos.W / e.SIZE_CHARAT_IMAGE) * aScale[2], sy = (pos.H / e.SIZE_CHARAT_IMAGE) * aScale[2];
    if (aReverse == 1) return 'translate(' + (pos.X + aW + xmgn) + ', ' + (pos.Y - ymgn) + '),scale(' + -Math.abs(sx) + ',' + sy + ')';
    return 'translate(' + (pos.X - xmgn) + ', ' + (pos.Y - ymgn) + '),scale(' + sx + ',' + sy + ')';
  };
  // パーツごとの g（中に 1 つ目のレイヤーの層、2 つ目からのレイヤーの層を重ねる）
  const roots = list.map((d) => {
    const g = svgEl('g', { class: e.ATTR_G_ROOT });
    bindData(g, d);
    if (svg) svg.appendChild(g);
    return g;
  });
  for (let li = 0; li < Math.max(1, aTgLayer.length); li++) {
    list.forEach((d, i) => {
      const layer = layerOf(d, aTgLayer, li);
      if (layer == undefined) return;
      roots[i].appendChild(layer);
      bindData(layer, d);
      layer.setAttribute('transform', ptransform(d));
    });
  }
  // 押す所（絵の上に透明な rect を重ねる）
  list.forEach((d, i) => {
    const pos = d.Pos!;
    const btn = svgEl('rect', { class: 'chibiPartsBtn', id: 'pb' + d.id, x: pos.X, y: pos.Y, width: pos.W, height: pos.H, rx: rsize, ry: rsize });
    bindData(btn, d);
    onClick(btn, () => clickPart(d, list, aCname, aLayerKey, aLayer, aCategory, aW, aH, pad));
    roots[i].appendChild(btn);
  });
  // パーツの番号（4 桁。先頭と末尾は大きく太字。キャラの一覧ではキャラの名前）
  const isChara = aCname == e.PARTS_CHARA;
  const pmini = isChara ? -1 : 0, pmaxi = isChara ? -1 : list.length - 1;
  const pw = aW / 20, phmin = aH / 10, phmax = aH / 8.5, ppos = aH / 20 + aH / 10;
  list.forEach((d, i) => {
    const pos = d.Pos!;
    const cdata = isChara ? e.getChara(d.id) : null;
    const named = cdata != null && looseEq(cdata.id, d.id);
    const end = pmini == i || pmaxi == i;
    const text = svgEl('text', { fill: 'orange', x: pos.X + pw, y: pos.Y + aH - ppos, 'text-anchor': 'start', 'font-size': end ? phmax : phmin });
    text.setAttribute('textLength', String(named ? pos.W - pw * 2 : pos.W / 3));
    text.setAttribute('lengthAdjust', 'spacing');
    text.setAttribute('font-weight', named ? 'normal' : end ? 'bolder' : 'normal');
    text.setAttribute('fill', named ? e.COLOR_PID_NAME : end ? e.COLOR_PID_STED : e.COLOR_PID_NORM);
    const label = named ? cdata!.name : ('0000' + d.id).slice(-4);
    text.textContent = label == null ? '' : label;
    bindData(text, d);
    roots[i].appendChild(text);
  });
  // スクロールバー再調整
  if (!e.isMobile) fx.scrollbarEnable(sbar);
  else if (!e.config('PARTS_LIST_BY_BUTTON', false)) e.btnEditParts.ON_PARTS();
  // 選んでいる印（チェックマーク。位置は selPartsStyle で動かす）
  if (svg) {
    const g = svgEl('g');
    const img = svgEl('image', { id: e.ATTR_SEL_CHECK });
    // d3 の attr("xlink:href") と同じく、XLink の名前空間の href（接頭辞なし）
    img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', e.FILE_CHECK);
    img.setAttribute('x', String(0 + (aW - aW / 4) / 2));
    img.setAttribute('y', String(0 + (aH - aH / 4) / 2));
    img.setAttribute('width', String(aW / 4));
    img.setAttribute('height', String(aH / 4));
    img.setAttribute('pointer-events', 'none');
    g.appendChild(img);
    svg.appendChild(g);
  }
  if (isChara) {
    // キャラの一覧は選んでいるものを出さない
    E().selPartsStyle(null);
    if (!e.isMobile) fx.scrollbarTo(sbar, [0, 0]);
    else fx.setScrollLeft(sbar, 0);
    return;
  }
  // 今付いているパーツのボタンまでスクロールして選んでいる枠を付ける（付いていなければ先頭）
  const hasG = e.selLayer.select('g').size() > 0 || (e.selLayer2 != null && e.selLayer2.select('g').size() > 0) || (e.selLayer3 != null && e.selLayer3.select('g').size() > 0);
  let hasId: number;
  let hasX = 0, hasY = 0;
  if (hasG) {
    hasId = E().getSelLayerId();
    const pcolpos = document.querySelector('rect.chibiPartsBtnR#pbr' + hasId);
    if (pcolpos) {
      const ptpos = Math.max(0, Math.round(Number(pcolpos.getAttribute('x'))));
      if (!e.isMobile) fx.scrollbarTo(sbar, [0, Math.max(0, ptpos - (fx.offsetLeft(sbar) as number))]);
      else fx.scrollLeftTo(sbar, ptpos);
      hasX = Number(pcolpos.getAttribute('x')) - pad.X;
      hasY = Number(pcolpos.getAttribute('y')) - pad.Y;
    }
  } else {
    hasId = Number(list[0].id);
    if (!e.isMobile) fx.scrollbarTo(sbar, [0, 0]);
    else fx.setScrollLeft(sbar, 0);
  }
  E().selPartsStyle(hasId, hasG ? hasX : 0, hasG ? hasY : 0, aW, aH);
}

/** パーツのボタンを押したとき（同じものをもう一度押すと一覧の先頭に戻す。キャラの一覧は確かめてからそのキャラを読む） */
function clickPart(
  d0: ListPart,
  list: ListPart[],
  aCname: string,
  aLayerKey: string,
  aLayer: string[],
  aCategory: Category,
  aW: number,
  aH: number,
  pad: { X: number; Y: number },
): void {
  const e = E(), sbar = e.DIV_F_PARTS_SC;
  if (C().nowChangeParts) return;
  let d = d0;
  if (aCname == e.PARTS_CHARA) {
    // キャラの一覧: 確かめてから読む（1 番は最後に保存したキャラ、無ければ初めのキャラ）
    const tt: Record<string, string> = typeof _t !== 'undefined' && _t ? _t : {};
    C().openDialog('confirm', [tt.load_confirm ? tt.load_confirm : 'Load character?', tt.load_warning ? tt.load_warning : '*Pressing OK will clear the current data'], () => {
      let isLoaded = false;
      if (d.id == 1) isLoaded = E().loadChara(E().ATTR_SAVEKEY, true);
      if (!isLoaded) E().load(d.id);
    });
    return;
  }
  // 同じものをもう一度押したら、一覧の先頭のパーツに戻す
  if (looseEq(e.selLayer.attr('paramParts'), d.id)) {
    d = list[0];
    const sel = 'rect.chibiPartsBtnR#pbr' + d.id;
    if (document.querySelector(sel)) {
      if (!e.isMobile) fx.scrollbarTo(sbar, [0, Math.max(0, (fx.offsetLeft(sel) as number) - (fx.offsetLeft(sbar) as number))]);
      else fx.scrollLeftTo(sbar, fx.offsetLeft(sel) as number);
    }
  }
  if (looseEq(e.selLayer.attr('paramParts'), d.id)) return;
  // 選んでいる枠を移し、パーツを差しかえる（差しかえ・パーツ番号・位置と拡大を戻す・色の塗り直しは中身＝段階 B3。差しかえの動きと後処理は changeParts）
  E().selPartsStyle(d.id, d.Pos!.X - pad.X, d.Pos!.Y - pad.Y, aW, aH);
  const svgArea = aLayer[0] == e.PARTS_BG ? e.svgBg : e.svgMake;
  const effectNo = aLayer[0] == e.PARTS_BG ? 2 : 1;
  const coreOps = E().coreOps();
  const target = d;
  C().changeParts(
    svgArea,
    aLayerKey,
    aLayer,
    effectNo,
    () => {
      // 差しかえたあと: 左右の選択・移動・反転を戻し、柄と色の入れかえを新しいパーツに掛け直す
      const en = E();
      en.btnEditLR.RESET(true);
      en.btnSubMove.RESET(true);
      // jakigan は戻さない（反転ボタンがキャラ全体を反転するので、戻すとキャラ全体がひっくり返る）
      if (en.config('RESET_REVERSE_ON_PARTS_CHANGE', true)) en.btnSubReverse.RESET(en.selCategory.REVERSE == 1);
      // 線の太さ（STROKE_WIDTH）: 新しいパーツの線を今の太さにする
      if (en.STROKE_WIDTH) en.btnSubThick.EXEC(aLayer[0]);
      // 柄の無いメーカー（MODE_PATTERN: false）は柄も色の入れかえも無いので掛け直さない
      if (en.MODE_PATTERN) {
        en.setPartsColorPattern(svgArea, aCategory, en.selLayer.attr('paramPtn'), en.selLayer.attr('paramPrev'), en.selLayer.attr('paramPtnMode'), en.selLayer.attr('paramCol'));
      }
      // 吹き出しの文字のカテゴリ
      if (en.PARTS_TEXT == aCname) en.btnMenuName.SETMODE(target.id);
      // 切り抜きの元か切り抜かれる側のパーツを替えたら、切り抜きを作り直す（nyanko3）
      if (en.PARTS_MASK != '' && (aCname == en.PARTS_MASK || en.PARTS_MASKED.indexOf(aCname) >= 0)) en.applyMask(svgArea, aLayerKey);
    },
    () => {
      coreOps.part(aCategory, target.id, effectNo == 1 && (typeof outlinemode === 'undefined' || !outlinemode));
    },
  );
}

/** 選んでいるカテゴリのレイヤー（selLayer → selLayer2 → selLayer3 の順）に付いているパーツの番号（どのレイヤーにも無く selLayer3 が null だと例外。前と同じ） */
export function getSelLayerId(): number {
  const e = E();
  const pid =
    e.selLayer.select('g').size() > 0
      ? e.selLayer.select('g')
      : e.selLayer2 != null && e.selLayer2.select('g').size() > 0
        ? e.selLayer2.select('g')
        : e.selLayer3!.select('g');
  return C().getPartsNoFromG(pid.attr('id'));
}

/** パーツの選んでいる枠と印（anId が null なら印を消す） */
export function selPartsStyle(anId: unknown, aX?: number, aY?: number, aW?: number, aH?: number): void {
  const e = E();
  document.querySelectorAll('rect.chibiPartsBtnR').forEach((el) => removeClass(el, e.ATTR_SELECTED));
  document.querySelectorAll('rect.chibiPartsBtn').forEach((el) => removeClass(el, e.ATTR_SELECTED));
  const checks = document.querySelectorAll('image#' + e.ATTR_SEL_CHECK);
  if (anId != null) {
    document.querySelectorAll('rect.chibiPartsBtnR#pbr' + Number(anId)).forEach((el) => addClass(el, e.ATTR_SELECTED));
    checks.forEach((el) => {
      el.setAttribute('x', String(aX! + (aW! - aW! / 4) / 2));
      el.setAttribute('y', String(aY! + (aH! - aH! / 4) / 2));
    });
  } else {
    checks.forEach((el) => el.setAttribute('display', 'none'));
  }
}

/**
 * カテゴリのパーツの SVG の g（id の頭の x・a・y が層）を、パーツ番号ごとに { id, x, a, y } にまとめて番号の順に返す（anId を渡すとその番号だけ）。
 * 中身の data.ts の partsList と同じ並び
 */
export function getPartsLayerList(aCname: string, aParts: { SVG: string }, anId?: unknown): ListPart[] {
  const all = C().getAllGfromSVG(aParts.SVG, aCname);
  const hash: Record<number, ListPart> = {};
  const keys: number[] = [];
  for (let i = 0; i < all.length; i++) {
    const lparam = all[i].id.substring(0, 1);
    const id = C().getPartsNoFromG(all[i].id);
    // 念のため番号を確かめる
    if (!(id >= 0 && (anId == undefined || looseEq(id, anId)))) continue;
    let part = hash[id];
    if (part == undefined) {
      part = { id };
      hash[id] = part;
      keys.push(id);
    }
    if (lparam == 'x') part.x = all[i];
    else if (lparam == 'a') part.a = all[i];
    else if (lparam == 'y') part.y = all[i];
  }
  const list = keys.map((k) => hash[k]);
  list.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return list;
}
